export type NotificationPermissionState = 'default' | 'granted' | 'denied';

export type Platform =
  | 'ios'
  | 'android'
  | 'desktop'
  | 'unknown';

export type BrowserFamily =
  | 'safari'
  | 'chrome'
  | 'firefox'
  | 'edge'
  | 'inapp_facebook'
  | 'inapp_instagram'
  | 'inapp_gmail'
  | 'inapp_messages'
  | 'other';

export type Context =
  | 'ios_safari_a2hs' // iPhone Safari, not installed, show Add to Home Screen guide
  | 'ios_standalone_enable_push' // iOS 16.4+ PWA, permission = default
  | 'ios_standalone_all_set' // iOS 16.4+ PWA, permission = granted
  | 'denied_explain' // permission denied
  | 'in_app_open_in_safari' // in-app browsers (Gmail, FB, IG, Messages)
  | 'ios_too_old' // iOS < 16.4
  | 'android_enable' // Android Chrome (or other), direct enable
  | 'desktop_enable'; // Desktop browsers

export type DetectionInput = {
  userAgent: string;
  isStandaloneDisplayMode: boolean;
  notificationPermission: NotificationPermissionState;
};

export type DetectionResult = {
  platform: Platform;
  browser: BrowserFamily;
  iosVersion?: { major: number; minor: number };
  context: Context;
};

function parseIOSVersion(ua: string): { major: number; minor: number } | undefined {
  // Examples:
  // iPhone; CPU iPhone OS 16_4 like Mac OS X
  // iPad; CPU OS 17_0 like Mac OS X
  const m =
    /OS (\d+)[._](\d+)(?:[._]\d+)? like Mac OS X/.exec(ua) ||
    /CPU (?:iPhone )?OS (\d+)[._](\d+)(?:[._]\d+)? like Mac OS X/.exec(ua);
  if (!m) return undefined;
  const major = Number(m[1]);
  const minor = Number(m[2]);
  if (Number.isNaN(major) || Number.isNaN(minor)) return undefined;
  return { major, minor };
}

function isIOS(ua: string): boolean {
  // iPhone|iPad|iPod, also account for iPadOS 13+ which may masquerade as Mac
  if (/(iPhone|iPad|iPod)/i.test(ua)) return true;
  // iPadOS masquerade: "Macintosh; Intel Mac OS X" plus "Mobile"
  if (/Macintosh;.*\bMobile\b/.test(ua) && /\bSafari\b/.test(ua)) return true;
  return false;
}

function isAndroid(ua: string): boolean {
  return /Android/i.test(ua);
}

function detectBrowserFamily(ua: string): BrowserFamily {
  const uaLower = ua.toLowerCase();

  // In-app browsers (iOS)
  if (/\bFBAN|FBAV\b/i.test(ua) || uaLower.includes('fbios')) return 'inapp_facebook';
  if (uaLower.includes('instagram')) return 'inapp_instagram';
  if (uaLower.includes('gsa') || uaLower.includes('gmail')) return 'inapp_gmail';
  if (uaLower.includes('imessage') || uaLower.includes('messages')) return 'inapp_messages';

  // Edge variants
  if (uaLower.includes('edg/')) return 'edge';
  if (uaLower.includes('edgios')) return 'edge';

  // Firefox variants
  if (uaLower.includes('firefox/') || uaLower.includes('fxios')) return 'firefox';

  // Chrome variants (iOS uses CriOS)
  if (uaLower.includes('chrome/') || uaLower.includes('crios')) return 'chrome';

  // Safari (exclude CriOS, FxiOS, EdgiOS)
  const isSafari =
    /\bSafari\b/.test(ua) && !/CriOS|FxiOS|EdgiOS|Chrome|OPR|Opera/i.test(ua);
  if (isSafari) return 'safari';

  return 'other';
}

function compareVersion(a: { major: number; minor: number }, b: { major: number; minor: number }) {
  if (a.major !== b.major) return a.major - b.major;
  return a.minor - b.minor;
}

export function detectContext(input: DetectionInput): DetectionResult {
  const { userAgent, isStandaloneDisplayMode, notificationPermission } = input;
  const ua = userAgent || '';

  const ios = isIOS(ua);
  const android = isAndroid(ua);
  const browser = detectBrowserFamily(ua);
  const iosVersion = ios ? parseIOSVersion(ua) : undefined;
  const supportsWebPushIOS = iosVersion ? compareVersion(iosVersion, { major: 16, minor: 4 }) >= 0 : false;

  // In-app browsers first: always show "Open in Safari"
  if (
    browser === 'inapp_facebook' ||
    browser === 'inapp_instagram' ||
    browser === 'inapp_gmail' ||
    browser === 'inapp_messages'
  ) {
    return {
      platform: ios ? 'ios' : android ? 'android' : 'unknown',
      browser,
      iosVersion,
      context: 'in_app_open_in_safari'
    };
  }

  if (ios) {
    if (!supportsWebPushIOS) {
      return {
        platform: 'ios',
        browser,
        iosVersion,
        context: 'ios_too_old'
      };
    }

    // iOS 16.4+:
    if (!isStandaloneDisplayMode) {
      // In Safari, not installed -> Add to Home Screen walkthrough
      return {
        platform: 'ios',
        browser,
        iosVersion,
        context: 'ios_safari_a2hs'
      };
    }

    // Standalone PWA
    if (notificationPermission === 'denied') {
      return {
        platform: 'ios',
        browser,
        iosVersion,
        context: 'denied_explain'
      };
    }
    if (notificationPermission === 'granted') {
      return {
        platform: 'ios',
        browser,
        iosVersion,
        context: 'ios_standalone_all_set'
      };
    }
    // default
    return {
      platform: 'ios',
      browser,
      iosVersion,
      context: 'ios_standalone_enable_push'
    };
  }

  if (android) {
    // Android: direct enable in-page
    if (notificationPermission === 'denied') {
      return {
        platform: 'android',
        browser,
        context: 'denied_explain'
      };
    }
    return {
      platform: 'android',
      browser,
      context: 'android_enable'
    };
  }

  // Desktop (or unknown): treat as desktop
  if (notificationPermission === 'denied') {
    return {
      platform: 'desktop',
      browser,
      context: 'denied_explain'
    };
  }
  return {
    platform: 'desktop',
    browser,
    context: 'desktop_enable'
  };
}

// Helper to capture environment into a pure input object
export function detectFromEnvironment(): DetectionResult {
  // display-mode: standalone or iOS navigator.standalone
  const isStandalone =
    (typeof window !== 'undefined' &&
      (window.matchMedia?.('(display-mode: standalone)').matches ?? false)) ||
    (typeof navigator !== 'undefined' && Boolean((navigator as any).standalone));

  const permission: NotificationPermissionState =
    typeof Notification !== 'undefined' ? Notification.permission : 'default';

  const ua =
    typeof navigator !== 'undefined' && navigator.userAgent
      ? navigator.userAgent
      : '';

  return detectContext({
    userAgent: ua,
    isStandaloneDisplayMode: isStandalone,
    notificationPermission: permission
  });
}

